import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Tabs } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { AppDispatch } from "../../../core/data/redux/store";
import {
  findDarCase,
  bypassDarContemplated,
  evaluateDarDefense,
  getSapEmployeeFetch,
} from "../../../core/data/redux/darSlice";
import { getPresignedUrl, uploadFile } from "../../../core/data/redux/awsBucketSlice";
import { all_routes } from "../../router/all_routes";
import { CLOSED_CASE_EXACT_ERROR, isClosedStage } from "../../../utils/darValidation";
import CharacterCounter from "../../../core/common/CharacterCounter";

const DarCaseDetailsDashboard: React.FC = () => {
  const { caseno } = useParams<{ caseno: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Employees may only view documents from the Contemplated and Pending
  // (Charges / Chargesheet) stages; Closed, Appeal, and Suspension documents
  // stay restricted to HR Admin / Nodal Officer / DA / AA logins.
  const storedUser = localStorage.getItem("user");
  const storedUserJson = storedUser && storedUser !== "undefined" ? JSON.parse(storedUser) : {};
  const isEmployeeViewer = (storedUserJson?.user_type || storedUserJson?.role) === "EMPLOYEE";

  const getTabFromPath = (path: string) => {
    if (path.includes("pending-details")) return "PENDING";
    if (path.includes("closed-details")) return "CLOSED";
    if (path.includes("appeal-details")) return "APPEAL";
    if (path.includes("sd-suspension-details")) return "SUSPENSION";
    return "CONTEMPLATED";
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  // Loading and data states for each stage
  const [loading, setLoading] = useState(false);
  const [generalCaseInfo, setGeneralCaseInfo] = useState<any>(null);

  const [contemplatedData, setContemplatedData] = useState<any>(null);
  const [pendingData, setPendingData] = useState<any>(null);
  const [closedData, setClosedData] = useState<any>(null);
  const [appealData, setAppealData] = useState<any>(null);
  const [suspensionData, setSuspensionData] = useState<any>(null);

  const [empDept, setEmpDept] = useState("");
  const [daDept, setDaDept] = useState("");

  // Status existence flags
  const [stagesAvailable, setStagesAvailable] = useState({
    CONTEMPLATED: false,
    PENDING: false,
    CLOSED: false,
    APPEAL: false,
    SUSPENSION: false,
  });

  // Modal and evaluation states
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [bypassReason, setBypassReason] = useState("");
  const [bypassing, setBypassing] = useState(false);

  const [showEvaluation, setShowEvaluation] = useState(false);
  const [evaluationData, setEvaluationData] = useState({
    decision: "REJECT_DEFENSE",
    remarks: "",
    orderFile: ""
  });
  const [uploadingOrder, setUploadingOrder] = useState(false);
  const [orderDocName, setOrderDocName] = useState("");
  const [evaluating, setEvaluating] = useState(false);

  const tabTypeMap: Record<string, number> = {
    CONTEMPLATED: 1,
    PENDING: 2,
    CLOSED: 3,
    APPEAL: 4,
    SUSPENSION: 5,
  };

  // Fetch individual stage details
  const fetchStageData = async (tab: string) => {
    if (!caseno) return;
    setLoading(true);
    try {
      const typeId = tabTypeMap[tab];
      const res = await dispatch(findDarCase({ caseno, type: typeId })).unwrap();
      const status = res?.status || res?.data?.status;
      if ((status === 200 || status === 201) && res?.data) {
        let data = res.data;
        if (Array.isArray(data)) {
          data = data.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || data[0];
        } else if (Array.isArray(data?.items)) {
          data = data.items.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || data.items[0];
        }
        if (tab === "CONTEMPLATED") {
          setContemplatedData(data);
          setStagesAvailable(prev => ({ ...prev, CONTEMPLATED: true }));
          if (!generalCaseInfo) {
            setGeneralCaseInfo({
              caseno: data.caseno,
              empname: data.empname,
              empno: data.empno,
              designation: data.designation,
              deptcode: data.deptcode,
              status: data.status,
            });
          }
        } else if (tab === "PENDING") {
          setPendingData(data);
          setStagesAvailable(prev => ({ ...prev, PENDING: true }));
          if (!generalCaseInfo) {
            setGeneralCaseInfo({
              caseno: data.caseno,
              empname: data.empname,
              empno: data.empno,
              designation: data.designation,
              deptcode: data.deptcode,
              status: data.status,
            });
          }
          if (data.chargesheetfile && !data.urls?.chargesheet_url) {
            dispatch(getPresignedUrl(data.chargesheetfile))
              .unwrap()
              .then((url: any) => {
                setPendingData((prev: any) => prev ? { ...prev, chargesheet_url: url as string } : null);
              })
              .catch(err => console.error("Failed to fetch chargesheet url:", err));
          }
        } else if (tab === "CLOSED") {
          setClosedData(data);
          setStagesAvailable(prev => ({ ...prev, CLOSED: true }));
          if (!generalCaseInfo) {
            setGeneralCaseInfo({
              caseno: data.caseno,
              empname: data.empname,
              empno: data.empno,
              designation: data.designation,
              deptcode: data.deptcode,
              status: data.status,
            });
          }
          if (data.finalorderfile && !data.urls?.final_order_url && !data.finalorder_url) {
            dispatch(getPresignedUrl(data.finalorderfile))
              .unwrap()
              .then((url: any) => {
                setClosedData((prev: any) => prev ? { ...prev, finalorder_url: url as string } : null);
              })
              .catch(err => console.error(err));
          }
          if (data.corrigendumfile && !data.urls?.corrigendum_url && !data.corrigendum_url) {
            dispatch(getPresignedUrl(data.corrigendumfile))
              .unwrap()
              .then((url: any) => {
                setClosedData((prev: any) => prev ? { ...prev, corrigendum_url: url as string } : null);
              })
              .catch(err => console.error(err));
          }
          const eoFile = data.eo_attachment || data.enqofficer_attachment;
          if (eoFile && !data.urls?.eo_attachment_url && !data.eo_attachment_url && !data.enqofficer_attachment_url) {
            dispatch(getPresignedUrl(eoFile))
              .unwrap()
              .then((url: any) => {
                setClosedData((prev: any) => prev ? { ...prev, eo_attachment_url: url as string, enqofficer_attachment_url: url as string } : null);
              })
              .catch(err => console.error(err));
          }

          const poFile = data.po_attachment || data.presofficer_attachment;
          if (poFile && !data.urls?.po_attachment_url && !data.po_attachment_url && !data.presofficer_attachment_url) {
            dispatch(getPresignedUrl(poFile))
              .unwrap()
              .then((url: any) => {
                setClosedData((prev: any) => prev ? { ...prev, po_attachment_url: url as string, presofficer_attachment_url: url as string } : null);
              })
              .catch(err => console.error(err));
          }
        } else if (tab === "APPEAL") {
          setAppealData(data);
          setStagesAvailable(prev => ({ ...prev, APPEAL: true }));
          if (!generalCaseInfo) {
            setGeneralCaseInfo({
              caseno: data.caseno,
              empname: data.empname,
              empno: data.empno,
              designation: data.designation,
              deptcode: data.deptcode,
              status: data.status,
            });
          }
          if (!closedData) {
            dispatch(findDarCase({ caseno, type: 3 }))
              .unwrap()
              .then((res: any) => {
                const status3 = res?.status || res?.data?.status;
                if ((status3 === 200 || status3 === 201) && res?.data) {
                  const cData = res.data;
                  setClosedData(cData);
                  setStagesAvailable(prev => ({ ...prev, CLOSED: true }));
                  if (cData.finalorderfile && !cData.urls?.final_order_url) {
                    dispatch(getPresignedUrl(cData.finalorderfile))
                      .unwrap()
                      .then((url: any) => {
                        setClosedData((prev: any) => prev ? { ...prev, finalorder_url: url as string } : null);
                      })
                      .catch(err => console.error(err));
                  }
                }
              })
              .catch(() => { });
          }
          if (data.aa_orderfile && !data.urls?.aa_order_url && !data.aa_order_url) {
            dispatch(getPresignedUrl(data.aa_orderfile))
              .unwrap()
              .then((url: any) => {
                setAppealData((prev: any) => prev ? { ...prev, aa_order_url: url as string } : null);
              })
              .catch(() => console.error("Failed to fetch order presigned URL"));
          }
          if (data.corrigendumfile && !data.urls?.corrigendum_url && !data.corrigendum_url) {
            dispatch(getPresignedUrl(data.corrigendumfile))
              .unwrap()
              .then((url: any) => {
                setAppealData((prev: any) => prev ? { ...prev, corrigendum_url: url as string } : null);
              })
              .catch(() => console.error("Failed to fetch corrigendum presigned URL"));
          }
          const appealDocKey = data.appeal_file || data.appeal_document || data.employee_appeal_file || data.appellant_file || data.docfile;
          if (appealDocKey && !data.urls?.appeal_url && !data.appeal_url) {
            dispatch(getPresignedUrl(appealDocKey))
              .unwrap()
              .then((url: any) => {
                setAppealData((prev: any) => prev ? { ...prev, appeal_url: url as string } : null);
              })
              .catch(() => console.error("Failed to fetch appeal doc presigned URL"));
          }
        } else if (tab === "SUSPENSION") {
          setSuspensionData(data);
          setStagesAvailable(prev => ({ ...prev, SUSPENSION: true }));
          if (!generalCaseInfo) {
            setGeneralCaseInfo({
              caseno: data.caseno,
              empname: data.empname,
              empno: data.empno,
              designation: data.designation,
              deptcode: data.deptcode,
              status: data.status,
            });
          }
          if (data.order_file && !data.urls?.order_file_url && !data.order_file_url) {
            dispatch(getPresignedUrl(data.order_file))
              .unwrap()
              .then((url: any) => {
                setSuspensionData((prev: any) => prev ? { ...prev, order_file_url: url as string } : null);
              })
              .catch(err => console.error("Failed to fetch suspension order presigned URL", err));
          }
          if (data.corrigendum_order && !data.urls?.corrigendum_url && !data.corrigendum_url) {
            dispatch(getPresignedUrl(data.corrigendum_order))
              .unwrap()
              .then((url: any) => {
                setSuspensionData((prev: any) => prev ? { ...prev, corrigendum_url: url as string } : null);
              })
              .catch(err => console.error("Failed to fetch suspension corrigendum presigned URL", err));
          }
          if (data.revocation_order && !data.urls?.revocation_url && !data.revocation_url) {
            dispatch(getPresignedUrl(data.revocation_order))
              .unwrap()
              .then((url: any) => {
                setSuspensionData((prev: any) => prev ? { ...prev, revocation_url: url as string } : null);
              })
              .catch(err => console.error("Failed to fetch suspension revocation presigned URL", err));
          }
          const csFile = data.chargesheetfile || data.chargesheet_file;
          if (csFile && !data.urls?.chargesheet_url && !data.chargesheet_url) {
            dispatch(getPresignedUrl(csFile))
              .unwrap()
              .then((url: any) => {
                setSuspensionData((prev: any) => prev ? { ...prev, chargesheet_url: url as string } : null);
              })
              .catch(err => console.error("Failed to fetch suspension chargesheet presigned URL", err));
          }

          if (!pendingData) {
            dispatch(findDarCase({ caseno, type: 2 }))
              .unwrap()
              .then((res: any) => {
                const pStatus = res?.status || res?.data?.status;
                if ((pStatus === 200 || pStatus === 201) && res?.data) {
                  const pData = res.data;
                  setPendingData(pData);
                  setStagesAvailable(prev => ({ ...prev, PENDING: true }));
                  if (pData.chargesheetfile && !pData.urls?.chargesheet_url) {
                    dispatch(getPresignedUrl(pData.chargesheetfile))
                      .unwrap()
                      .then((url: any) => {
                        setPendingData((prev: any) => prev ? { ...prev, chargesheet_url: url as string } : null);
                      })
                      .catch(() => {});
                  }
                }
              })
              .catch(() => {});
          }

          if (!contemplatedData) {
            dispatch(findDarCase({ caseno, type: 1 }))
              .unwrap()
              .then((res: any) => {
                const cStatus = res?.status || res?.data?.status;
                if ((cStatus === 200 || cStatus === 201) && res?.data) {
                  setContemplatedData(res.data);
                  setStagesAvailable(prev => ({ ...prev, CONTEMPLATED: true }));
                }
              })
              .catch(() => {});
          }
        }
      } else {
        if (tab === "CONTEMPLATED") setContemplatedData(null);
        else if (tab === "PENDING") setPendingData(null);
        else if (tab === "CLOSED") setClosedData(null);
        else if (tab === "APPEAL") setAppealData(null);
        else if (tab === "SUSPENSION") setSuspensionData(null);
      }
    } catch (err) {
      console.error(`Error loading ${tab} details:`, err);
      if (tab === "CONTEMPLATED") setContemplatedData(null);
      else if (tab === "PENDING") setPendingData(null);
      else if (tab === "CLOSED") setClosedData(null);
      else if (tab === "APPEAL") setAppealData(null);
      else if (tab === "SUSPENSION") setSuspensionData(null);
    } finally {
      setLoading(false);
    }
  };

  // Perform initial fetch for active tab and general info
  useEffect(() => {
    toast.dismiss();
    if (caseno) {
      const initialTab = getTabFromPath(location.pathname);
      setActiveTab(initialTab);
      fetchStageData(initialTab);
      // Fetch Contemplated as fallback to get employee summary details
      if (initialTab !== "CONTEMPLATED") {
        dispatch(findDarCase({ caseno, type: 1 }))
          .unwrap()
          .then((res: any) => {
            if (res?.data) {
              setGeneralCaseInfo({
                caseno: res.data.caseno,
                empname: res.data.empname,
                empno: res.data.empno,
                designation: res.data.designation,
                deptcode: res.data.deptcode,
                status: res.data.status,
              });
            }
          })
          .catch(() => { });
      }
    }
    return () => {
      toast.dismiss();
    };
  }, [caseno, location.pathname]);

  useEffect(() => {
    const empNo = generalCaseInfo?.empno || contemplatedData?.empno || pendingData?.empno;
    if (empNo && !empDept) {
      dispatch(getSapEmployeeFetch({ emp_no: empNo }))
        .unwrap()
        .then((res: any) => {
          if (res?.data?.deptname) {
            setEmpDept(res.data.deptname);
          }
        })
        .catch((err: any) => console.error("Error fetching subject employee department name:", err));
    }
  }, [generalCaseInfo?.empno, contemplatedData?.empno, pendingData?.empno, dispatch, empDept]);

  useEffect(() => {
    const daEmpNo = contemplatedData?.daempno || pendingData?.daempno;
    if (daEmpNo && !daDept) {
      dispatch(getSapEmployeeFetch({ emp_no: daEmpNo, role: "DA" }))
        .unwrap()
        .then((res: any) => {
          if (res?.data?.deptname) {
            setDaDept(res.data.deptname);
          }
        })
        .catch((err: any) => console.error("Error fetching DA department name:", err));
    }
  }, [contemplatedData?.daempno, pendingData?.daempno, dispatch, daDept]);

  // Tab switcher
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    fetchStageData(key);
  };

  const handleBypassSubmit = async () => {
    if (!bypassReason.trim()) {
      toast.error("Bypass reason is mandatory.");
      return;
    }
    setBypassing(true);
    try {
      const response = await dispatch(
        bypassDarContemplated({ case_no: caseno, reason: bypassReason })
      ).unwrap();
      if (response?.status === 200 || response?.status === 201) {
        toast.success(response?.message || "Case successfully bypassed to Pending stage.");
        setShowBypassModal(false);
        navigate(`/dar-cases/pending-details/${caseno}`);
      } else {
        toast.error(response?.message || "Failed to bypass case.");
      }
    } catch (error: any) {
      toast.error(error?.message || "Error bypassing case.");
    } finally {
      setBypassing(false);
    }
  };

  const handleOrderFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Invalid file format. Only PDF is allowed.");
      return;
    }

    setUploadingOrder(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        const result = await dispatch(
          uploadFile({ filestring: base64, mime_type: file.type, module: "dar" })
        );
        if (uploadFile.fulfilled.match(result)) {
          const key =
            result.payload?.key ||
            result.payload?.data?.key ||
            result.payload?.url ||
            result.payload?.data?.url ||
            "";
          if (key) {
            setEvaluationData(prev => ({ ...prev, orderFile: key }));
            setOrderDocName(file.name);
            toast.success("Intermediate order file uploaded successfully.");
          } else {
            toast.error("Failed to retrieve document path from server.");
          }
        } else {
          toast.error("Failed to upload document.");
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingOrder(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEvaluateSubmit = async () => {
    if (!evaluationData.remarks.trim()) {
      toast.error("Evaluation remarks are mandatory.");
      return;
    }
    if (!evaluationData.orderFile) {
      toast.error("Please attach the signed intermediate order file.");
      return;
    }

    setEvaluating(true);
    try {
      const payload = {
        caseno: caseno,
        action_intent: evaluationData.decision,
        remarks: evaluationData.remarks,
        intermediate_order_file: evaluationData.orderFile,
      };

      const response = await dispatch(evaluateDarDefense(payload)).unwrap();
      if (response?.status === 200 || response?.status === 201) {
        toast.success(response?.message || "Defense evaluation submitted successfully.");
        setShowEvaluation(false);
        if (evaluationData.decision === "REJECT_DEFENSE") {
          navigate(`/dar-cases/closed-form?caseno=${caseno}`);
        } else {
          fetchStageData("PENDING");
        }
      } else {
        toast.error(response?.message || "Failed to submit evaluation.");
      }
    } catch (error: any) {
      toast.error(error?.message || "Error submitting defense evaluation.");
    } finally {
      setEvaluating(false);
    }
  };

  const viewDocument = (url: string) => {
    if (url) {
      window.open(url, "_blank");
    } else {
      toast.error("Document URL not available.");
    }
  };

  const handleViewDocOrKey = async (fileOrUrl: string) => {
    if (!fileOrUrl) {
      toast.error("Document reference not available.");
      return;
    }
    if (fileOrUrl.startsWith("http://") || fileOrUrl.startsWith("https://") || fileOrUrl.startsWith("blob:")) {
      window.open(fileOrUrl, "_blank");
      return;
    }
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileOrUrl)).unwrap();
      toast.dismiss(toastId);
      if (url) window.open(url, "_blank");
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const getHistoryList = (data: any, stageType: string = "contemplated"): any[] => {
    if (!data) return [];
    
    // 1. Array candidates
    const arrayCandidates = [
      data.history,
      data.previousData,
      data.previous_data,
      data.history_data,
      data.historyLogs,
      data.logs,
      data.edits,
      data.edit_history,
      data.editHistory,
      data.changes,
      data.auditLogs,
      data.audit_logs,
      data.previous_records,
      data.previousRecords,
      data.versions,
      data.data?.history,
      data.data?.previousData,
      data.data?.previous_data,
      data.data?.history_data
    ];

    const norm = (v: any) => (v === undefined || v === null ? "" : String(v).trim().toLowerCase());

    for (const candidate of arrayCandidates) {
      if (Array.isArray(candidate) && candidate.length > 0) {
        const hasVersionMarkers = candidate.some(
          (item: any) => item && (item.version_type || item.versionType || item.is_current !== undefined || item.isCurrent !== undefined)
        );

        let filtered = candidate.filter((item: any) => {
          if (!item) return false;
          const vType = String(item.version_type || item.versionType || "").toLowerCase();
          if (vType === "current") return false;
          if (item.is_current === true || item.isCurrent === true || item.current === true) return false;

          // Stage specific duplicate and field presence check
          if (stageType === "closed") {
            const itemType = norm(item.penalty_type || item.penaltyType);
            const itemDesc = norm(item.penalty_desc || item.penaltyDesc || item.remarks || item.reason);
            const itemFile = norm(item.finalorderfile || item.final_order_file || item.finalorder_url || item.urls?.final_order_url);
            const itemAA = norm(item.aa_name || item.aa_emp_no);
            const itemOutcome = norm(item.outcome_type || item.outcomeType);

            const currType = norm(data.penalty_type || data.penaltyType);
            const currDesc = norm(data.penalty_desc || data.penaltyDesc || data.remarks || data.reason);
            const currFile = norm(data.finalorderfile || data.final_order_file || data.finalorder_url || data.urls?.final_order_url);
            const currAA = norm(data.aa_name || data.aa_emp_no);
            const currOutcome = norm(data.outcome_type || data.outcomeType);

            const hasClosedFields = !!(itemType || itemDesc || itemFile || itemAA || itemOutcome);
            if (!hasClosedFields) return false; // Filter out entries without closed stage fields

            let isDuplicate = true;
            if (itemType && currType && itemType !== currType) isDuplicate = false;
            if (itemDesc && currDesc && itemDesc !== currDesc) isDuplicate = false;
            if (itemFile && currFile && itemFile !== currFile) isDuplicate = false;
            if (itemAA && currAA && itemAA !== currAA) isDuplicate = false;
            if (itemOutcome && currOutcome && itemOutcome !== currOutcome) isDuplicate = false;

            if (isDuplicate) return false;
          } else if (stageType === "pending") {
            const itemChargeType = norm(item.charge_type || item.chargetype);
            const itemCsDate = norm(item.chargesheetdate || item.chargesheet_date);
            const itemRemarks = norm(item.remarks || item.reason);
            const itemCsFile = norm(item.chargesheetfile || item.chargesheet_file);

            const currChargeType = norm(data.charge_type || data.chargetype);
            const currCsDate = norm(data.chargesheetdate || data.chargesheet_date);
            const currRemarks = norm(data.remarks || data.reason);
            const currCsFile = norm(data.chargesheetfile || data.chargesheet_file);

            const hasPendingFields = !!(itemChargeType || itemCsDate || itemRemarks || itemCsFile || item.charges);
            if (!hasPendingFields) return false;

            let isDuplicate = true;
            if (itemChargeType && currChargeType && itemChargeType !== currChargeType) isDuplicate = false;
            if (itemCsDate && currCsDate && itemCsDate !== currCsDate) isDuplicate = false;
            if (itemRemarks && currRemarks && itemRemarks !== currRemarks) isDuplicate = false;
            if (itemCsFile && currCsFile && itemCsFile !== currCsFile) isDuplicate = false;

            if (isDuplicate) return false;
          } else if (stageType === "contemplated") {
            const itemDa = norm(item.daname || item.da_name);
            const itemEmp = norm(item.daempno || item.da_emp_no);
            const itemAppFile = norm(item.approvalfile || item.approval_file);

            const currDa = norm(data.daname || data.da_name);
            const currEmp = norm(data.daempno || data.da_emp_no);
            const currAppFile = norm(data.approvalfile || data.approval_file);

            const hasContemplatedFields = !!(itemDa || itemEmp || itemAppFile || item.casedescription);
            if (!hasContemplatedFields) return false;

            let isDuplicate = true;
            if (itemDa && currDa && itemDa !== currDa) isDuplicate = false;
            if (itemEmp && currEmp && itemEmp !== currEmp) isDuplicate = false;
            if (itemAppFile && currAppFile && itemAppFile !== currAppFile) isDuplicate = false;

            if (isDuplicate) return false;
          } else if (stageType === "appeal") {
            const itemAA = norm(item.aaname || item.aa_name || item.aaName);
            const itemDecision = norm(item.aa_decision || item.aaDecision || item.decision);
            const itemAppealDate = norm(item.appeal_date || item.appealDate || item.submission_date);
            const itemRemarks = norm(item.remarks || item.appeal_grounds || item.grounds || item.reason || item.appeal_desc || item.change_reason || item.edit_reason);
            const itemAAFile = norm(item.aa_orderfile || item.aa_order_file || item.aa_order_url || item.urls?.aa_order_url);
            const itemCorrFile = norm(item.corrigendumfile || item.corrigendum_file || item.corrigendum_url || item.urls?.corrigendum_url);
            const itemAppealFile = norm(item.appeal_file || item.employee_appeal_file || item.appeal_url || item.urls?.appeal_url);

            const currAA = norm(data.aaname || data.aa_name || data.aaName);
            const currDecision = norm(data.aa_decision || data.aaDecision || data.decision);
            const currAppealDate = norm(data.appeal_date || data.appealDate || data.submission_date);
            const currRemarks = norm(data.remarks || data.appeal_grounds || data.grounds || data.reason || data.appeal_desc || data.change_reason || data.edit_reason);
            const currAAFile = norm(data.aa_orderfile || data.aa_order_file || data.aa_order_url || data.urls?.aa_order_url);
            const currCorrFile = norm(data.corrigendumfile || data.corrigendum_file || data.corrigendum_url || data.urls?.corrigendum_url);
            const currAppealFile = norm(data.appeal_file || data.employee_appeal_file || data.appeal_url || data.urls?.appeal_url);

            const itemSig = `${itemAA}|${itemDecision}|${itemAppealDate}|${itemRemarks}|${itemAAFile}|${itemCorrFile}|${itemAppealFile}`;
            const currSig = `${currAA}|${currDecision}|${currAppealDate}|${currRemarks}|${currAAFile}|${currCorrFile}|${currAppealFile}`;

            if (itemSig === currSig) return false;
          }

          if (vType === "previous") return true;
          if (item.is_previous === true || item.isPrevious === true) return true;
          if (hasVersionMarkers && vType && vType !== "previous") return false;
          return true;
        });

        if (filtered.length > 0) return filtered;
      }
    }

    // 2. Object candidates
    const objectCandidates = [
      data.previous,
      data.previousDetails,
      data.oldData,
      data.old_data,
      data.prev_data,
      data.data?.previous,
      data.data?.oldData
    ];

    for (const candidate of objectCandidates) {
      if (candidate && typeof candidate === "object" && !Array.isArray(candidate) && Object.keys(candidate).length > 0) {
        if (!candidate.version_type || String(candidate.version_type).toLowerCase() === "previous") {
          return [candidate];
        }
      }
    }

    // 3. Check individual fields on data if present
    if (data.version_type && String(data.version_type).toLowerCase() !== "previous") {
      return [];
    }

    const prevFields: any = {};
    if (data.prev_daname || data.old_daname || data.previous_daname || data.prev_da_name) {
      prevFields.daname = data.prev_daname || data.old_daname || data.previous_daname || data.prev_da_name;
    }
    if (data.prev_dadesignation || data.old_dadesignation || data.previous_dadesignation) {
      prevFields.dadesignation = data.prev_dadesignation || data.old_dadesignation || data.previous_dadesignation;
    }
    if (data.prev_daempno || data.old_daempno || data.previous_daempno) {
      prevFields.daempno = data.prev_daempno || data.old_daempno || data.previous_daempno;
    }
    if (data.prev_dadepartment || data.old_dadepartment || data.previous_dadepartment) {
      prevFields.dadepartment = data.prev_dadepartment || data.old_dadepartment || data.previous_dadepartment;
    }
    if (data.prev_casedescription || data.old_casedescription || data.previous_casedescription) {
      prevFields.casedescription = data.prev_casedescription || data.old_casedescription || data.previous_casedescription;
    }
    if (data.prev_casetype || data.old_casetype || data.previous_casetype) {
      prevFields.casetype = data.prev_casetype || data.old_casetype || data.previous_casetype;
    }
    if (data.prev_approvalfile || data.old_approvalfile || data.previous_approvalfile) {
      prevFields.approvalfile = data.prev_approvalfile || data.old_approvalfile || data.previous_approvalfile;
    }
    if (data.updateddate || data.updated_date || data.updated_at || data.updatedAt || data.modified_at || data.edit_date || data.createddate || data.created_date) {
      prevFields.updateddate = data.updateddate || data.updated_date || data.updated_at || data.updatedAt || data.modified_at || data.edit_date || data.createddate || data.created_date;
    }

    if (data.prev_chargesheetdate || data.old_chargesheetdate || data.previous_chargesheetdate || data.prev_chargesheet_date) {
      prevFields.chargesheetdate = data.prev_chargesheetdate || data.old_chargesheetdate || data.previous_chargesheetdate || data.prev_chargesheet_date;
    }
    if (data.prev_charge_type || data.old_charge_type || data.previous_charge_type || data.prev_chargetype) {
      prevFields.charge_type = data.prev_charge_type || data.old_charge_type || data.previous_charge_type || data.prev_chargetype;
    }
    if (data.prev_chargesheetfile || data.old_chargesheetfile || data.previous_chargesheetfile || data.prev_chargesheet_file) {
      prevFields.chargesheetfile = data.prev_chargesheetfile || data.old_chargesheetfile || data.previous_chargesheetfile || data.prev_chargesheet_file;
    }
    if (data.prev_charges || data.old_charges || data.previous_charges) {
      prevFields.charges = data.prev_charges || data.old_charges || data.previous_charges;
    }
    if (data.prev_remarks || data.old_remarks || data.previous_remarks || data.prev_reason || data.old_reason) {
      prevFields.remarks = data.prev_remarks || data.old_remarks || data.previous_remarks || data.prev_reason || data.old_reason;
    }

    if (Object.keys(prevFields).length > 0) {
      return [prevFields];
    }

    return [];
  };

  const renderHistorySection = (stageData: any, stageType: string = "contemplated") => {
    const historyItems = getHistoryList(stageData, stageType);
    if (!historyItems || historyItems.length === 0) return null;

    return (
      <div className="card shadow-sm border-0 rounded-3 mb-4">
        <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <i className="ti ti-history text-info fs-5"></i> 5. Case Modification & Previous History
          </h5>
          <span className="badge bg-info-subtle text-info fw-semibold px-3 py-2 rounded-pill">
            {historyItems.length} Previous Modification{historyItems.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="card-body p-4">
          <div className="timeline-history">
            {historyItems.map((item: any, idx: number) => {
              const changeDate = item.updateddate || item.updated_date || item.updated_at || item.updatedAt || item.createddate || item.created_date || item.created_at || item.createdAt || item.changed_at || item.changedAt || item.date || item.timestamp || item.edit_date || item.editedAt || item.modified_at;
              let formattedDate = "Date Not Available";
              if (changeDate) {
                const m = moment(changeDate);
                if (m.isValid()) {
                  formattedDate = m.format("DD MMM YYYY, hh:mm A");
                } else {
                  formattedDate = String(changeDate);
                }
              }
              const appFileKey = item.approvalfile || item.approval_file || item.approval_url || item.file || item.approval_doc;
              const csFileKey = item.chargesheetfile || item.chargesheet_file || item.chargesheet_url;
              const finalOrderFileKey = item.finalorderfile || item.final_order_file || item.finalorder_url || item.urls?.final_order_url;
              const corrigendumFileKey = item.corrigendumfile || item.corrigendum_url || item.urls?.corrigendum_url;
              const aaOrderFileKey = item.aa_orderfile || item.aa_order_file || item.aa_order_url || item.urls?.aa_order_url;
              const appealFileKey = item.appeal_file || item.employee_appeal_file || item.appeal_url || item.urls?.appeal_url;

              return (
                <div key={idx} className="border rounded-3 p-3 mb-3 bg-light position-relative shadow-sm">
                  <div className="d-flex flex-wrap justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-secondary text-white fw-bold d-flex align-items-center gap-1">
                        <i className="ti ti-history"></i> Previous Modification #{historyItems.length - idx}
                      </span>
                      <span className="fw-semibold text-dark small d-flex align-items-center gap-1">
                        <i className="ti ti-calendar text-primary"></i> Changed Date: {formattedDate}
                      </span>
                    </div>
                  </div>

                  <div className="row g-3">
                    {/* 1. CONTEMPLATED STAGE HISTORY */}
                    {stageType === "contemplated" && (
                      <>
                        {(item.daname || item.dadesignation || item.daempno || item.dadepartment || item.da_department) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-gavel text-danger"></i> Disciplinary Authority (Previous)
                            </div>
                            <div className="row g-2 bg-white p-2 rounded border">
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Name</span>
                                <span className="fw-medium text-dark small">{item.daname || item.da_name || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Designation</span>
                                <span className="fw-medium text-dark small">{item.dadesignation || item.da_designation || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Employee No</span>
                                <span className="fw-medium text-dark small">{item.daempno || item.da_emp_no || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Department</span>
                                <span className="fw-medium text-dark small">{item.dadepartment || item.da_department || "-"}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {(item.casedescription || item.casetype || item.case_description || item.allegations) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-file-description text-warning"></i> Case Allegations & Description (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border">
                              {item.casetype && (
                                <div className="mb-1">
                                  <span className="text-muted small me-2">Case Type:</span>
                                  <span className="badge bg-secondary-subtle text-secondary fw-semibold">{item.casetype}</span>
                                </div>
                              )}
                              <div className="text-dark small" style={{ whiteSpace: "pre-wrap" }}>
                                {item.casedescription || item.case_description || item.allegations || "-"}
                              </div>
                            </div>
                          </div>
                        )}

                        {appFileKey && (
                          <div className="col-md-6 col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-paperclip text-success"></i> Previous Approval Document
                            </div>
                            <div className="bg-white p-2 rounded border d-flex align-items-center justify-content-between">
                              <span className="text-truncate small text-muted me-2" style={{ maxWidth: "250px" }}>
                                {typeof appFileKey === "string" ? appFileKey.split("/").pop() : "Document"}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() => handleViewDocOrKey(appFileKey)}
                              >
                                <i className="ti ti-eye"></i> View File
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* 2. PENDING STAGE HISTORY */}
                    {stageType === "pending" && (
                      <>
                        {(item.chargesheetdate || item.chargesheet_date || item.charge_type || item.chargetype || csFileKey || item.charges) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-file-text text-info"></i> Chargesheet Details (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              {(item.charge_type || item.chargetype) && (
                                <div className="col-md-4 col-6">
                                  <span className="text-muted small d-block">Charge Type</span>
                                  <span className="badge bg-warning-subtle text-warning-emphasis fw-semibold">{item.charge_type || item.chargetype}</span>
                                </div>
                              )}
                              {(item.chargesheetdate || item.chargesheet_date) && (
                                <div className="col-md-4 col-6">
                                  <span className="text-muted small d-block">Chargesheet Date</span>
                                  <span className="fw-medium text-dark small">
                                    {moment(item.chargesheetdate || item.chargesheet_date).isValid()
                                      ? moment(item.chargesheetdate || item.chargesheet_date).format("DD MMM YYYY")
                                      : String(item.chargesheetdate || item.chargesheet_date)}
                                  </span>
                                </div>
                              )}
                              {csFileKey && (
                                <div className="col-md-4 col-12">
                                  <span className="text-muted small d-block">Previous Chargesheet Document</span>
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="text-truncate small text-muted me-2" style={{ maxWidth: "180px" }}>
                                      {typeof csFileKey === "string" ? csFileKey.split("/").pop() : "Document"}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 py-0 px-2"
                                      onClick={() => handleViewDocOrKey(csFileKey)}
                                    >
                                      <i className="ti ti-eye"></i> View File
                                    </button>
                                  </div>
                                </div>
                              )}
                              {item.charges && (
                                <div className="col-12 mt-2 border-top pt-2">
                                  <span className="text-muted small d-block mb-1">Previous Charges / Articles of Charge</span>
                                  <div className="fw-medium text-dark small">
                                    {(() => {
                                      const chargesData = item.charges;
                                      if (!chargesData) return "-";
                                      let parsed = chargesData;
                                      if (typeof chargesData === "string") {
                                        const trimmed = chargesData.trim();
                                        if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
                                          try { parsed = JSON.parse(trimmed); } catch {}
                                        }
                                      }
                                      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                                        parsed = [parsed];
                                      }
                                      if (Array.isArray(parsed) && parsed.length > 0) {
                                        return parsed.map((c: any) => {
                                          let itemObj = c;
                                          if (typeof c === "string") {
                                            const t = c.trim();
                                            if (t.startsWith("{") && t.endsWith("}")) {
                                              try { itemObj = JSON.parse(t); } catch {}
                                            }
                                          }
                                          if (typeof itemObj === "object" && itemObj !== null) {
                                            return itemObj.charge_description || itemObj.description || itemObj.title || itemObj.name || itemObj.label || itemObj.charge_code || JSON.stringify(itemObj);
                                          }
                                          return String(c);
                                        }).filter(Boolean).join(", ");
                                      }
                                      return String(chargesData);
                                    })()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {(item.casedescription || item.casetype || item.case_description || item.allegations) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-file-description text-warning"></i> Case Allegations & Description (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border">
                              {item.casetype && (
                                <div className="mb-1">
                                  <span className="text-muted small me-2">Case Type:</span>
                                  <span className="badge bg-secondary-subtle text-secondary fw-semibold">{item.casetype}</span>
                                </div>
                              )}
                              <div className="text-dark small" style={{ whiteSpace: "pre-wrap" }}>
                                {item.casedescription || item.case_description || item.allegations || "-"}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* 3. CLOSED STAGE HISTORY */}
                    {stageType === "closed" && (
                      <>
                        {/* Outcome & Penalty Details */}
                        {(item.outcome_type || item.penalty_type || item.penalty_desc || item.penalty_start_date || item.exonerated_flag || item.guilty_flag !== undefined) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-award text-success"></i> Outcome & Penalty Details (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">Outcome / Finding</span>
                                <span className="fw-semibold text-dark small">
                                  {item.outcome_type || (item.exonerated_flag ? "Exonerated" : item.guilty_flag ? "Guilty" : "-")}
                                </span>
                              </div>
                              {(item.penalty_type || item.penaltyType) && (
                                <div className="col-md-3 col-6">
                                  <span className="text-muted small d-block">Penalty Type</span>
                                  <span className="badge bg-danger-subtle text-danger fw-semibold">{item.penalty_type || item.penaltyType}</span>
                                </div>
                              )}
                              {(item.penalty_start_date || item.exoneration_date) && (
                                <div className="col-md-3 col-6">
                                  <span className="text-muted small d-block">Effective Date</span>
                                  <span className="fw-medium text-dark small">
                                    {moment(item.penalty_start_date || item.exoneration_date).isValid()
                                      ? moment(item.penalty_start_date || item.exoneration_date).format("DD MMM YYYY")
                                      : String(item.penalty_start_date || item.exoneration_date)}
                                  </span>
                                </div>
                              )}
                              {(item.was_suspended || item.susp_treatment) && (
                                <div className="col-md-3 col-6">
                                  <span className="text-muted small d-block">Suspension Treatment</span>
                                  <span className="fw-medium text-dark small">{item.susp_treatment || item.was_suspended || "-"}</span>
                                </div>
                              )}
                              {(item.penalty_desc || item.penaltyDesc) && (
                                <div className="col-12 mt-2 border-top pt-2">
                                  <span className="text-muted small d-block mb-1">Penalty Description</span>
                                  <div className="fw-medium text-dark small" style={{ whiteSpace: "pre-wrap" }}>
                                    {item.penalty_desc || item.penaltyDesc}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Inquiry & Appellate Officers */}
                        {(item.enqofficer_name || item.presofficer_name || item.aa_name || item.io_name || item.po_name) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-users text-primary"></i> Officers & Appellate Authority (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              {(item.enqofficer_name || item.io_name) && (
                                <div className="col-md-4 col-12">
                                  <span className="text-muted small d-block">Enquiry Officer</span>
                                  <span className="fw-medium text-dark small">{item.enqofficer_name || item.io_name} ({item.enqofficer_designation || item.enqofficer_empno || "-"})</span>
                                </div>
                              )}
                              {(item.presofficer_name || item.po_name) && (
                                <div className="col-md-4 col-12">
                                  <span className="text-muted small d-block">Presenting Officer</span>
                                  <span className="fw-medium text-dark small">{item.presofficer_name || item.po_name} ({item.presofficer_designation || item.presofficer_empno || "-"})</span>
                                </div>
                              )}
                              {item.aa_name && (
                                <div className="col-md-4 col-12">
                                  <span className="text-muted small d-block">Appellate Authority</span>
                                  <span className="fw-medium text-dark small">{item.aa_name} ({item.aa_designation || item.aa_empno || "-"})</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Final Order & Corrigendum Files */}
                        {(finalOrderFileKey || corrigendumFileKey) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-paperclip text-success"></i> Previous Order Documents
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              {finalOrderFileKey && (
                                <div className="col-md-6 col-12 d-flex align-items-center justify-content-between">
                                  <span className="text-truncate small text-muted me-2" style={{ maxWidth: "200px" }}>
                                    Final Order: {typeof finalOrderFileKey === "string" ? finalOrderFileKey.split("/").pop() : "Document"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-success d-flex align-items-center gap-1 py-0 px-2"
                                    onClick={() => handleViewDocOrKey(finalOrderFileKey)}
                                  >
                                    <i className="ti ti-eye"></i> View Order
                                  </button>
                                </div>
                              )}
                              {corrigendumFileKey && (
                                <div className="col-md-6 col-12 d-flex align-items-center justify-content-between">
                                  <span className="text-truncate small text-muted me-2" style={{ maxWidth: "200px" }}>
                                    Corrigendum: {typeof corrigendumFileKey === "string" ? corrigendumFileKey.split("/").pop() : "Document"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 py-0 px-2"
                                    onClick={() => handleViewDocOrKey(corrigendumFileKey)}
                                  >
                                    <i className="ti ti-eye"></i> View Corrigendum
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* 4. APPEAL STAGE HISTORY */}
                    {stageType === "appeal" && (
                      <>
                        {(item.aaname || item.aa_name || item.aaempno || item.aa_decision || item.decision || item.appeal_date) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-user-check text-success"></i> Appellate Authority & Decision (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              {(item.aaname || item.aa_name) && (
                                <div className="col-md-4 col-6">
                                  <span className="text-muted small d-block">Appellate Authority</span>
                                  <span className="fw-medium text-dark small">
                                    {item.aaname || item.aa_name} ({item.aadesignation || item.aa_designation || item.aaempno || item.aa_empno || "-"})
                                  </span>
                                </div>
                              )}
                              {(item.appeal_date || item.submission_date) && (
                                <div className="col-md-4 col-6">
                                  <span className="text-muted small d-block">Appeal Date</span>
                                  <span className="fw-medium text-dark small">
                                    {moment(item.appeal_date || item.submission_date).isValid()
                                      ? moment(item.appeal_date || item.submission_date).format("DD MMM YYYY")
                                      : String(item.appeal_date || item.submission_date)}
                                  </span>
                                </div>
                              )}
                              {(item.aa_decision || item.decision) && (
                                <div className="col-md-4 col-6">
                                  <span className="text-muted small d-block">AA Decision</span>
                                  <span className="badge bg-light text-dark border px-2 py-1 small fw-semibold">
                                    {item.aa_decision || item.decision}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {(item.remarks || item.appeal_grounds || item.grounds || item.reason || item.appeal_desc) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-info-circle text-primary"></i> Grounds of Appeal / Remarks (Previous)
                            </div>
                            <div className="bg-white p-2 rounded border text-dark small" style={{ whiteSpace: "pre-wrap" }}>
                              {item.remarks || item.appeal_grounds || item.grounds || item.reason || item.appeal_desc}
                            </div>
                          </div>
                        )}

                        {(aaOrderFileKey || appealFileKey || corrigendumFileKey) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-paperclip text-success"></i> Previous Appeal Documents
                            </div>
                            <div className="bg-white p-2 rounded border row g-2">
                              {aaOrderFileKey && (
                                <div className="col-md-6 col-12 d-flex align-items-center justify-content-between">
                                  <span className="text-truncate small text-muted me-2" style={{ maxWidth: "200px" }}>
                                    Appellate Order: {typeof aaOrderFileKey === "string" ? aaOrderFileKey.split("/").pop() : "Document"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 py-0 px-2"
                                    onClick={() => handleViewDocOrKey(aaOrderFileKey)}
                                  >
                                    <i className="ti ti-eye"></i> View Order
                                  </button>
                                </div>
                              )}
                              {appealFileKey && (
                                <div className="col-md-6 col-12 d-flex align-items-center justify-content-between">
                                  <span className="text-truncate small text-muted me-2" style={{ maxWidth: "200px" }}>
                                    Appeal Document: {typeof appealFileKey === "string" ? appealFileKey.split("/").pop() : "Document"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 py-0 px-2"
                                    onClick={() => handleViewDocOrKey(appealFileKey)}
                                  >
                                    <i className="ti ti-eye"></i> View Document
                                  </button>
                                </div>
                              )}
                              {corrigendumFileKey && (
                                <div className="col-md-6 col-12 d-flex align-items-center justify-content-between">
                                  <span className="text-truncate small text-muted me-2" style={{ maxWidth: "200px" }}>
                                    Corrigendum: {typeof corrigendumFileKey === "string" ? corrigendumFileKey.split("/").pop() : "Document"}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 py-0 px-2"
                                    onClick={() => handleViewDocOrKey(corrigendumFileKey)}
                                  >
                                    <i className="ti ti-eye"></i> View Corrigendum
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* FALLBACK FOR OTHER STAGES (APPEAL / SUSPENSION) */}
                    {(stageType !== "contemplated" && stageType !== "pending" && stageType !== "closed") && (
                      <>
                        {(item.daname || item.dadesignation || item.daempno || item.dadepartment || item.da_department) && (
                          <div className="col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-gavel text-danger"></i> Disciplinary Authority (Previous)
                            </div>
                            <div className="row g-2 bg-white p-2 rounded border">
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Name</span>
                                <span className="fw-medium text-dark small">{item.daname || item.da_name || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Designation</span>
                                <span className="fw-medium text-dark small">{item.dadesignation || item.da_designation || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Employee No</span>
                                <span className="fw-medium text-dark small">{item.daempno || item.da_emp_no || "-"}</span>
                              </div>
                              <div className="col-md-3 col-6">
                                <span className="text-muted small d-block">DA Department</span>
                                <span className="fw-medium text-dark small">{item.dadepartment || item.da_department || "-"}</span>
                              </div>
                            </div>
                          </div>
                        )}
                        {appFileKey && (
                          <div className="col-md-6 col-12">
                            <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                              <i className="ti ti-paperclip text-success"></i> Previous Document
                            </div>
                            <div className="bg-white p-2 rounded border d-flex align-items-center justify-content-between">
                              <span className="text-truncate small text-muted me-2" style={{ maxWidth: "250px" }}>
                                {typeof appFileKey === "string" ? appFileKey.split("/").pop() : "Document"}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                onClick={() => handleViewDocOrKey(appFileKey)}
                              >
                                <i className="ti ti-eye"></i> View File
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* 5. Reason for Modification */}
                    {(item.reason || item.remarks || item.change_reason || item.edit_reason) && (
                      <div className="col-md-6 col-12">
                        <div className="fw-bold text-dark small mb-2 d-flex align-items-center gap-1">
                          <i className="ti ti-info-circle text-primary"></i> Reason for Modification
                        </div>
                        <div className="bg-white p-2 rounded border text-dark small">
                          {item.reason || item.remarks || item.change_reason || item.edit_reason}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderNotReached = (stageName: string) => (
    <div className="card shadow-sm border-0 rounded-3 text-center py-5">
      <div className="card-body">
        <div className="mb-3 text-muted">
          <i className="ti ti-lock-open-off text-muted" style={{ fontSize: "3rem" }}></i>
        </div>
        <h4 className="fw-bold text-dark mb-2">Stage Not Initiated</h4>
        <p className="text-muted mb-0">
          This D&AR case has not reached the <strong>{stageName}</strong> stage yet.
        </p>
      </div>
    </div>
  );

  return (
    <div className="page-wrapper" style={{ minHeight: "100vh" }}>
      <div className="content">
        {/* GLOBAL DOSSIER HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <h3 className="page-title mb-1 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="ti ti-briefcase text-primary"></i> D&AR Case Dossier
              {generalCaseInfo?.status && (
                <span
                  className={`badge bg-${generalCaseInfo.status === "CLOSED"
                    ? "success"
                    : generalCaseInfo.status === "PENDING"
                      ? "warning"
                      : "info"
                    } fs-6 ms-2`}
                >
                  {generalCaseInfo.status}
                </span>
              )}
            </h3>
            {generalCaseInfo && (
              <div className="text-muted small mt-1">
                <strong>Case Reference:</strong> {generalCaseInfo.caseno} &nbsp;&nbsp;•&nbsp;&nbsp;
                <strong>Employee:</strong> {generalCaseInfo.empname || "N/A"} ({generalCaseInfo.empno})
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <Link
              to="/dar-cases/overview"
              className="btn btn-light shadow-sm d-flex align-items-center gap-2 px-4 rounded-pill"
            >
              <i className="ti ti-arrow-left"></i> Back to Dashboard
            </Link>
          </div>
        </div>

        {/* TABBED INTERFACE CONTAINER */}
        <div className="mb-4">
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            type="card"
            items={[
              {
                label: (
                  <span>
                    <i className="ti ti-file-info me-1"></i> Contemplated
                  </span>
                ),
                key: "CONTEMPLATED",
                children: loading && activeTab === "CONTEMPLATED" ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : contemplatedData ? (
                  <div>
                    {/* SECTION 1: Subject Employee Details */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-user me-2 text-primary"></i> 1. Subject Employee Details
                        </h5>
                        <Link
                          to={`/dar-cases/new?caseno=${caseno}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="ti ti-edit me-1"></i> Edit details
                        </Link>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Employee Name</div>
                            <div className="fw-semibold text-dark">{contemplatedData.empname || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Employee No</div>
                            <div className="fw-semibold text-dark">{contemplatedData.empno || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Designation</div>
                            <div className="fw-semibold text-dark">{contemplatedData.designation || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Department</div>
                            <div className="fw-semibold text-dark">{empDept || contemplatedData.deptcode || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Date of Joining</div>
                            <div className="fw-semibold text-dark">
                              {contemplatedData.doj ? moment(contemplatedData.doj).format("DD MMM YYYY") : "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Basic Pay</div>
                            <div className="fw-semibold text-dark">{contemplatedData.basicpay || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Last Promo Date</div>
                            <div className="fw-semibold text-dark">
                              {contemplatedData.lastpromodate
                                ? moment(contemplatedData.lastpromodate).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Annual Increment Date</div>
                            <div className="fw-semibold text-dark">
                              {contemplatedData.annualincdate
                                ? moment(contemplatedData.annualincdate).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 2: Disciplinary Authority */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-gavel me-2 text-danger"></i> 2. Disciplinary Authority (DA)
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Name</div>
                            <div className="fw-semibold text-dark">{contemplatedData.daname || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Designation</div>
                            <div className="fw-semibold text-dark">{contemplatedData.dadesignation || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Employee No</div>
                            <div className="fw-semibold text-dark">{contemplatedData.daempno || "-"}</div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Department</div>
                            <div className="fw-semibold text-dark">{daDept || contemplatedData.dadepartment || contemplatedData.da_department || "-"}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 3: Case Description */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-file-description me-2 text-warning"></i> 3. Case Description
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Case Type</div>
                            <div className="fw-semibold text-dark">{contemplatedData.casetype || "-"}</div>
                          </div>
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Brief Description of Allegations</div>
                            <div
                              className="bg-light p-3 rounded text-dark"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {contemplatedData.casedescription || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 4: Attachments */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-paperclip me-2 text-success"></i> 4. Attachments & Workflow
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-3 align-items-center">
                          <div className="col-md-6">
                            <div className="text-muted small fw-medium mb-2">Approval Sheet Document</div>
                            {contemplatedData.approvalfile ? (
                              <div className="d-flex align-items-center gap-3">
                                <button
                                  onClick={() =>
                                    viewDocument(
                                      contemplatedData.urls?.approval_url || contemplatedData.url
                                    )
                                  }
                                  className="btn btn-outline-primary d-flex align-items-center gap-2"
                                >
                                  <i className="ti ti-file-text fs-5"></i> View Document
                                </button>
                                <span className="text-success small fw-medium">
                                  <i className="ti ti-check me-1"></i>
                                  {contemplatedData.approvalfile.split("/").pop()}
                                </span>
                              </div>
                            ) : (
                              <div className="text-muted small">No file attached</div>
                            )}
                          </div>
                        </div>


                      </div>
                    </div>

                    {renderHistorySection(contemplatedData)}
                  </div>
                ) : (
                  renderNotReached("Contemplated")
                ),
              },
              {
                label: (
                  <span>
                    <i className="ti ti-alert-circle me-1"></i> Pending Stage
                  </span>
                ),
                key: "PENDING",
                children: loading && activeTab === "PENDING" ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : pendingData ? (
                  <div>
                    {/* SECTION 1: Subject Employee Details (for Direct Entry) */}
                    {!stagesAvailable.CONTEMPLATED && (
                      <div className="card shadow-sm border-0 rounded-3 mb-4">
                        <div className="card-header bg-white border-bottom p-3">
                          <h5 className="mb-0 fw-bold">
                            <i className="ti ti-user me-2 text-primary"></i> 1. Subject Employee Details
                          </h5>
                        </div>
                        <div className="card-body p-4">
                          <div className="row g-4">
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Employee Name</div>
                              <div className="fw-semibold text-dark">{pendingData.empname || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Employee No</div>
                              <div className="fw-semibold text-dark">{pendingData.empno || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Designation</div>
                              <div className="fw-semibold text-dark">{pendingData.designation || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Department</div>
                              <div className="fw-semibold text-dark">{empDept || pendingData.deptcode || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Date of Joining</div>
                              <div className="fw-semibold text-dark">
                                {pendingData.doj ? moment(pendingData.doj).format("DD MMM YYYY") : "-"}
                              </div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Basic Pay</div>
                              <div className="fw-semibold text-dark">{pendingData.basicpay || "-"}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SECTION 2: Disciplinary Authority (for Direct Entry) */}
                    {!stagesAvailable.CONTEMPLATED && (
                      <div className="card shadow-sm border-0 rounded-3 mb-4">
                        <div className="card-header bg-white border-bottom p-3">
                          <h5 className="mb-0 fw-bold">
                            <i className="ti ti-gavel me-2 text-danger"></i> 2. Disciplinary Authority (DA)
                          </h5>
                        </div>
                        <div className="card-body p-4">
                          <div className="row g-4">
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">DA Name</div>
                              <div className="fw-semibold text-dark">{pendingData.daname || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">DA Designation</div>
                              <div className="fw-semibold text-dark">{pendingData.dadesignation || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">DA Employee No</div>
                              <div className="fw-semibold text-dark">{pendingData.daempno || "-"}</div>
                            </div>
                            <div className="col-md-3">
                              <div className="text-muted small fw-medium mb-1">Department</div>
                              <div className="fw-semibold text-dark">{daDept || pendingData.dadepartment || pendingData.da_department || "-"}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* CASE DESCRIPTION */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold text-dark">
                          <i className="ti ti-file-description me-2 text-warning"></i> Case Description
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Case Type</div>
                            <div className="fw-semibold text-dark">{pendingData.casetype || "-"}</div>
                          </div>
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Brief Description of Allegations</div>
                            <div
                              className="bg-light p-3 rounded text-dark"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {pendingData.casedescription || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 1: Charge Sheet Details */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-file-text me-2 text-primary"></i> Charge Sheet Details
                        </h5>
                        <div className="d-flex gap-2">
                          {!showEvaluation && (
                            <button
                              className="btn btn-sm btn-warning text-dark fw-medium d-flex align-items-center gap-1"
                              onClick={() => setShowEvaluation(true)}
                            >
                              <i className="ti ti-gavel"></i> Evaluate Defense
                            </button>
                          )}
                          <Link
                            to={`/dar-cases/pending-form?caseno=${caseno}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="ti ti-edit me-1"></i> Edit form
                          </Link>
                        </div>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Type of Charges</div>
                            <div className="fw-semibold text-dark">
                              {pendingData.type_of_charges || pendingData.charge_type || pendingData.chargetype || "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">
                              Date of Charge Sheet Issuance
                            </div>
                            <div className="fw-semibold text-dark">
                              {pendingData.chargesheetdate
                                ? moment(pendingData.chargesheetdate).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Charge Sheet Document</div>
                            {pendingData.chargesheet_url || pendingData.chargesheetfile ? (
                              <div className="d-flex align-items-center gap-3 mt-1">
                                <a
                                  href={pendingData.chargesheet_url || pendingData.urls?.chargesheet_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                                >
                                  <i className="ti ti-eye"></i> View Document
                                </a>
                              </div>
                            ) : (
                              <div className="text-muted small">No file attached</div>
                            )}
                          </div>
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-2">Charges</div>
                            {pendingData.charges && pendingData.charges.length > 0 ? (
                              <div className="list-group list-group-flush border rounded-3 overflow-hidden shadow-none mb-0">
                                {pendingData.charges.map((charge: any, idx: number) => (
                                  <div key={idx} className="list-group-item text-dark fw-medium d-flex align-items-start gap-2 bg-light-subtle py-2">
                                    <span className="text-secondary fw-bold" style={{ minWidth: "20px" }}>{idx + 1}.</span>
                                    <span>{charge.charge_description}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-muted small">No charges added</div>
                            )}
                          </div>
                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Remarks</div>
                            <div className="bg-light p-3 rounded text-dark" style={{ whiteSpace: "pre-wrap" }}>
                              {pendingData.remarks || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Defense Evaluation Panel */}
                    {showEvaluation && (
                      <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                        <div className="bg-dark text-white p-3 d-flex align-items-center gap-2">
                          <i className="ti ti-lock fs-5"></i>
                          <h6 className="mb-0 fw-bold" style={{ letterSpacing: "1px" }}>
                            EMPLOYEE DEFENSE EVALUATION CONTROL
                          </h6>
                        </div>
                        <div className="card-body p-4 bg-white border">
                          <div className="mb-4">
                            <label className="form-label fw-semibold text-dark">Action Decision</label>
                            <div className="btn-group w-100 shadow-sm" role="group">
                              <input
                                type="radio"
                                className="btn-check"
                                name="decision"
                                id="btnAccept"
                                checked={evaluationData.decision === "ACCEPT_DEFENSE"}
                                onChange={() =>
                                  setEvaluationData({ ...evaluationData, decision: "ACCEPT_DEFENSE" })
                                }
                              />
                              <label className="btn btn-outline-success py-3 fw-bold" htmlFor="btnAccept">
                                ACCEPT DEFENSE (Exonerate)
                              </label>

                              <input
                                type="radio"
                                className="btn-check"
                                name="decision"
                                id="btnReject"
                                checked={evaluationData.decision === "REJECT_DEFENSE"}
                                onChange={() =>
                                  setEvaluationData({ ...evaluationData, decision: "REJECT_DEFENSE" })
                                }
                              />
                              <label className="btn btn-outline-danger py-3 fw-bold" htmlFor="btnReject">
                                REJECT DEFENSE (Proceed to Penalty)
                              </label>
                            </div>
                          </div>

                          <div className="mb-4">
                            <label className="form-label fw-semibold text-dark">
                              DA Evaluation Remarks <span className="text-danger">*</span>
                            </label>
                            <textarea
                              className="form-control bg-light border"
                              rows={4}
                              placeholder="Provide justification for acceptance/rejection of employee defense..."
                              value={evaluationData.remarks}
                              onChange={(e) =>
                                setEvaluationData({ ...evaluationData, remarks: e.target.value })
                              }
                              required
                            ></textarea>
                          </div>

                          <div className="mb-4">
                            <label className="form-label fw-semibold text-dark">
                              Attach Signed Intermediate Order File <span className="text-danger">*</span>
                            </label>
                            <div className="card border border-dashed shadow-none rounded-3 bg-light">
                              <div className="card-body text-center p-4">
                                <input
                                  type="file"
                                  id="orderDoc"
                                  className="d-none"
                                  accept=".pdf"
                                  onChange={handleOrderFileUpload}
                                  disabled={uploadingOrder}
                                />
                                {!orderDocName ? (
                                  <>
                                    <div className="mb-3 text-primary">
                                      <i className="ti ti-cloud-upload fs-1"></i>
                                    </div>
                                    <h6 className="mb-1">Drag and Drop / Browse Document</h6>
                                    <p className="text-muted small mb-3">PDF Only, Max 5MB</p>
                                    <button
                                      type="button"
                                      className="btn btn-primary rounded-pill px-4"
                                      onClick={() => document.getElementById("orderDoc")?.click()}
                                      disabled={uploadingOrder}
                                    >
                                      {uploadingOrder ? (
                                        <>
                                          <i className="ti ti-loader fa-spin me-2"></i>Uploading...
                                        </>
                                      ) : (
                                        "Browse File"
                                      )}
                                    </button>
                                  </>
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center flex-column">
                                    <div className="text-success mb-2">
                                      <i className="ti ti-file-check fs-1"></i>
                                    </div>
                                    <h6 className="mb-1">{orderDocName}</h6>
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm rounded-pill px-3 mt-2"
                                      onClick={() => {
                                        setOrderDocName("");
                                        setEvaluationData(p => ({ ...p, orderFile: "" }));
                                      }}
                                    >
                                      <i className="ti ti-trash me-1"></i>Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-end gap-3 pt-3 border-top mt-4">
                            <button
                              className="btn btn-light border px-4 py-2 fw-medium rounded-pill"
                              disabled={evaluating}
                              onClick={() => setShowEvaluation(false)}
                            >
                              Close Panel
                            </button>
                            <button
                              className="btn btn-primary px-5 py-2 fw-medium rounded-pill shadow-sm d-flex align-items-center"
                              onClick={handleEvaluateSubmit}
                              disabled={evaluating}
                            >
                              {evaluating ? (
                                <>
                                  <i className="ti ti-loader fa-spin me-2"></i>Submitting...
                                </>
                              ) : (
                                "Submit Evaluation"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {renderHistorySection(pendingData, "pending")}
                  </div>
                ) : (
                  renderNotReached("Pending")
                ),
              },
              {
                label: (
                  <span>
                    <i className="ti ti-circle-check me-1"></i> Closed Stage
                  </span>
                ),
                key: "CLOSED",
                children: loading && activeTab === "CLOSED" ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : closedData ? (
                  <div>
                    {/* OUTCOME & PENALTY DETAILS CARD */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-award me-2 text-success"></i> Outcome & Penalty Details
                        </h5>
                        <Link
                          to={`/dar-cases/closed-form?caseno=${caseno}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="ti ti-edit me-1"></i> Edit Closed Details
                        </Link>
                      </div>
                      <div className="card-body p-4">
                        <div className="d-flex align-items-center gap-5 mb-4">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={closedData.outcome_type === "EXONERATED" || !!closedData.exonerated_flag}
                              disabled
                            />
                            <label className="form-check-label fw-semibold text-dark">Exonerated</label>
                          </div>

                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={closedData.outcome_type === "GUILTY" || (!closedData.outcome_type && !!closedData.guilty_flag)}
                              disabled
                            />
                            <label className="form-check-label fw-semibold text-dark">Guilty</label>
                          </div>

                          {closedData.exoneration_date && (
                            <div className="ms-3">
                              <span className="text-muted small fw-medium me-2">Exoneration Date:</span>
                              <span className="fw-semibold text-dark">
                                {dayjs(closedData.exoneration_date).format("DD MMM YYYY")}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="row g-4">
                          <div className="col-md-3 col-6">
                            <div className="text-muted small fw-medium mb-1">Outcome / Finding</div>
                            <div>
                              {closedData.exonerated_flag ? (
                                <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill fw-semibold">
                                  <i className="ti ti-circle-check me-1"></i> Exonerated
                                </span>
                              ) : (
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill fw-semibold">
                                  <i className="ti ti-alert-triangle me-1"></i> Guilty / Penalty Imposed
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-md-3 col-6">
                            <div className="text-muted small fw-medium mb-1">Penalty Type</div>
                            <div className="fw-semibold text-dark fs-6">{closedData.penalty_type || "-"}</div>
                          </div>
                          <div className="col-md-3 col-6">
                            <div className="text-muted small fw-medium mb-1">Penalty Start / Effective Date</div>
                            <div className="fw-semibold text-dark">
                              {closedData.penalty_start_date
                                ? moment(closedData.penalty_start_date).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-3 col-6">
                            <div className="text-muted small fw-medium mb-1">Penalty End Date</div>
                            <div className="fw-semibold text-dark">
                              {closedData.penalty_end_date
                                ? moment(closedData.penalty_end_date).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="text-muted small fw-medium mb-1">Penalty Description</div>
                            <div className="bg-light p-3 rounded text-dark" style={{ whiteSpace: "pre-wrap" }}>
                              {closedData.penalty_desc || "-"}
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="text-muted small fw-medium mb-1">Was Suspended?</div>
                            <div className="fw-semibold text-dark">{closedData.was_suspended || "-"}</div>
                          </div>
                          <div className="col-md-6">
                            <div className="text-muted small fw-medium mb-1">Suspension Period Treatment</div>
                            <div className="fw-semibold text-dark">{closedData.susp_treatment || "-"}</div>
                          </div>
                        </div>

                        {/* INQUIRY OFFICERS & APPELLATE AUTHORITY CARD */}
                        <div className="mt-4 pt-4 border-top">
                          <h5 className="mb-3 text-primary fw-bold">Inquiry Officers & Appellate Authority</h5>
                          <div className="row g-3">
                            {(() => {
                              const eoName = closedData.enqofficer_name || closedData.io_name;
                              const eoDesig = closedData.enqofficer_designation;
                              const eoEmpNo = closedData.enqofficer_empno || closedData.io_emp_no;

                              const poName = closedData.presofficer_name || closedData.po_name;
                              const poDesig = closedData.presofficer_designation;
                              const poEmpNo = closedData.presofficer_empno || closedData.po_emp_no;

                              const aaName = closedData.aa_name || closedData.appellate_authority;
                              const aaDesig = closedData.aa_designation;
                              const aaEmpNo = closedData.aa_empno;

                              return (
                                <>
                                  <div className="col-md-4">
                                    <div className="bg-light p-3 rounded border">
                                      <div className="text-muted small fw-bold text-uppercase mb-1">Enquiry Officer</div>
                                      <div className="fw-bold text-dark">{eoName || "Not Specified"}</div>
                                      {eoDesig && <div className="text-muted small">{eoDesig}</div>}
                                      <div className="text-muted small mt-1">
                                        {eoEmpNo ? `Emp No: ${eoEmpNo}` : "Emp No: -"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="bg-light p-3 rounded border">
                                      <div className="text-muted small fw-bold text-uppercase mb-1">Presenting Officer</div>
                                      <div className="fw-bold text-dark">{poName || "Not Specified"}</div>
                                      {poDesig && <div className="text-muted small">{poDesig}</div>}
                                      <div className="text-muted small mt-1">
                                        {poEmpNo ? `Emp No: ${poEmpNo}` : "Emp No: -"}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="bg-light p-3 rounded border">
                                      <div className="text-muted small fw-bold text-uppercase mb-1">Designated Appellate Authority</div>
                                      <div className="fw-bold text-dark">{aaName || "Not Specified"}</div>
                                      {aaDesig && <div className="text-muted small">{aaDesig}</div>}
                                      <div className="text-muted small mt-1">
                                        {aaEmpNo ? `Emp No: ${aaEmpNo}` : "Emp No: -"}
                                      </div>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* FINAL ORDER & ATTACHED DOCUMENTS SECTION */}
                        <div className="mt-4 pt-4 border-top">
                          <h5 className="mb-3 text-primary fw-bold">Final Order & Attached Documents</h5>
                          <div className="d-flex flex-wrap gap-3">
                            {/* Final Order */}
                            {(closedData.finalorderfile || closedData.finalorder_url || closedData.urls?.final_order_url) && (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    closedData.finalorder_url || closedData.urls?.final_order_url || closedData.finalorderfile
                                  )
                                }
                                className="btn btn-outline-success d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-check fs-5"></i> View Final Order Document
                              </button>
                            )}

                            {/* Corrigendum */}
                            {(closedData.corrigendumfile || closedData.corrigendum_url || closedData.urls?.corrigendum_url) && (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    closedData.corrigendum_url || closedData.urls?.corrigendum_url || closedData.corrigendumfile
                                  )
                                }
                                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-symlink fs-5"></i> View Corrigendum Document
                              </button>
                            )}

                            {/* Enquiry Officer Attachment */}
                            {(closedData.eo_attachment || closedData.enqofficer_attachment || closedData.eo_attachment_url || closedData.enqofficer_attachment_url || closedData.urls?.eo_attachment_url || closedData.urls?.enqofficer_attachment_url) && (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    closedData.eo_attachment_url || closedData.enqofficer_attachment_url || closedData.urls?.eo_attachment_url || closedData.urls?.enqofficer_attachment_url || closedData.eo_attachment || closedData.enqofficer_attachment
                                  )
                                }
                                className="btn btn-outline-primary d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-description fs-5"></i> View Enquiry Officer Report
                              </button>
                            )}

                            {/* Presenting Officer Attachment */}
                            {(closedData.po_attachment || closedData.presofficer_attachment || closedData.po_attachment_url || closedData.presofficer_attachment_url || closedData.urls?.po_attachment_url || closedData.urls?.presofficer_attachment_url) && (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    closedData.po_attachment_url || closedData.presofficer_attachment_url || closedData.urls?.po_attachment_url || closedData.urls?.presofficer_attachment_url || closedData.po_attachment || closedData.presofficer_attachment
                                  )
                                }
                                className="btn btn-outline-info d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-text fs-5"></i> View Presenting Officer Report
                              </button>
                            )}

                            {!(closedData.finalorderfile || closedData.finalorder_url || closedData.urls?.final_order_url) &&
                              !(closedData.corrigendumfile || closedData.corrigendum_url || closedData.urls?.corrigendum_url) &&
                              !(closedData.enqofficer_attachment || closedData.enqofficer_attachment_url || closedData.urls?.enqofficer_attachment_url) &&
                              !(closedData.presofficer_attachment || closedData.presofficer_attachment_url || closedData.urls?.presofficer_attachment_url) && (
                                <span className="text-muted fst-italic">No documents attached for closed stage.</span>
                              )}
                          </div>
                        </div>
                        {renderHistorySection(closedData, "closed")}
                      </div>
                    </div>
                  </div>
                ) : (
                  renderNotReached("Closed")
                ),
              },
              {
                label: (
                  <span>
                    <i className="ti ti-gavel me-1"></i> Appeal
                  </span>
                ),
                key: "APPEAL",
                children: loading && activeTab === "APPEAL" ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : appealData ? (
                  <div>
                    {/* ORIGINAL PENALTY REFERENCE CARD */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-info-square-rounded me-2 text-warning"></i> Original Penalty
                          Reference
                        </h5>
                        <Link
                          to={`/dar-cases/appeal-form?caseno=${caseno}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <i className="ti ti-edit me-1"></i> Edit Appeal details
                        </Link>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Penalty Type</div>
                            <div className="fw-semibold text-dark">
                              {appealData.penalty_type ||
                                appealData.closedDetails?.penalty_type ||
                                appealData.closed_details?.penalty_type ||
                                closedData?.penalty_type ||
                                "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Penalty Start Date</div>
                            <div className="fw-semibold text-dark">
                              {(appealData.penalty_start_date ||
                                appealData.closedDetails?.penalty_start_date ||
                                appealData.closed_details?.penalty_start_date ||
                                closedData?.penalty_start_date)
                                ? dayjs(
                                  appealData.penalty_start_date ||
                                  appealData.closedDetails?.penalty_start_date ||
                                  appealData.closed_details?.penalty_start_date ||
                                  closedData?.penalty_start_date
                                ).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Penalty Description</div>
                            <div className="fw-semibold text-dark">
                              {appealData.penalty_desc ||
                                appealData.closedDetails?.penalty_desc ||
                                appealData.closed_details?.penalty_desc ||
                                closedData?.penalty_desc ||
                                "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* APPELLATE AUTHORITY & DECISION CARD */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-user-check me-2 text-success"></i> Appellate Authority &
                          Decision
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4 mb-4">
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Appellate Authority</div>
                            <div className="fw-semibold text-dark">
                              {appealData.aaname || appealData.aa_name || appealData.closedDetails?.aa_name || "-"}
                              {(appealData.aadesignation || appealData.aa_designation || appealData.closedDetails?.aa_designation) ? ` (${appealData.aadesignation || appealData.aa_designation || appealData.closedDetails?.aa_designation})` : ""}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">AA Emp No</div>
                            <div className="fw-semibold text-dark">
                              {appealData.aaempno || appealData.aa_empno || appealData.closedDetails?.aa_empno || "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Appeal Date</div>
                            <div className="fw-semibold text-dark">
                              {(appealData.appeal_date || appealData.submission_date || appealData.created_at)
                                ? dayjs(appealData.appeal_date || appealData.submission_date || appealData.created_at).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">AA Decision</div>
                            <div className="fw-semibold text-dark">
                              <span className="badge bg-light text-dark border px-3 py-2">
                                {appealData.aa_decision || appealData.decision || "-"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {(appealData.remarks || appealData.appeal_grounds || appealData.grounds || appealData.reason || appealData.appeal_desc) && (
                          <div className="mb-4 p-3 bg-light rounded border">
                            <div className="text-muted small fw-medium mb-1">Grounds of Appeal / Remarks</div>
                            <div className="text-dark fw-medium" style={{ whiteSpace: "pre-wrap" }}>
                              {appealData.remarks || appealData.appeal_grounds || appealData.grounds || appealData.reason || appealData.appeal_desc}
                            </div>
                          </div>
                        )}

                        {/* INQUIRY OFFICERS SECTION FOR APPEAL STAGE */}
                        {(() => {
                          const eoName = closedData?.eo_name || closedData?.enqofficer_name || closedData?.enquiry_officer_name || "";
                          const eoEmpNo = closedData?.eo_empno || closedData?.enqofficer_empno || "";
                          const eoDesig = closedData?.eo_designation || closedData?.enqofficer_designation || "";
                          const eoSource = closedData?.eo_source || closedData?.enqofficer_source || (eoEmpNo ? "Internal" : "");
                          const eoRemarks = closedData?.eo_remarks || closedData?.enqofficer_remarks || "";

                          const poName = closedData?.po_name || closedData?.presofficer_name || closedData?.presenting_officer_name || "";
                          const poEmpNo = closedData?.po_empno || closedData?.presofficer_empno || "";
                          const poDesig = closedData?.po_designation || closedData?.presofficer_designation || "";
                          const poSource = closedData?.po_source || closedData?.presofficer_source || (poEmpNo ? "Internal" : "");
                          const poRemarks = closedData?.po_remarks || closedData?.presofficer_remarks || "";

                          const isMajor = appealData.penalty_type?.toLowerCase().includes("major") || closedData?.penalty_type?.toLowerCase().includes("major");
                          const hasOfficerData = !!(eoName || eoEmpNo || poName || poEmpNo);

                          if (!isMajor && !hasOfficerData) return null;

                          return (
                            <div className="mb-4 pt-3 border-top">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                <h6 className="mb-0 text-secondary fw-bold">
                                  <i className="ti ti-users me-2 text-primary"></i> Inquiry Officers (Enquiry Officer & Presenting Officer)
                                </h6>
                                {isMajor && (
                                  <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-1 small">
                                    Major Penalty Case
                                  </span>
                                )}
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6">
                                  <div className="p-3 bg-light rounded border">
                                    <div className="text-primary small fw-bold mb-1">Enquiry Officer</div>
                                    <div className="fw-semibold text-dark">
                                      {eoName || (eoEmpNo ? `Emp No: ${eoEmpNo}` : "Not Specified")}
                                      {eoDesig ? ` (${eoDesig})` : ""}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      {eoEmpNo ? `Emp No: ${eoEmpNo}` : "Emp No: -"}
                                      {eoSource ? ` | Source: ${eoSource}` : ""}
                                    </div>
                                    {eoRemarks && (
                                      <div className="text-muted small mt-2">
                                        <strong>Remarks:</strong> {eoRemarks}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="col-md-6">
                                  <div className="p-3 bg-light rounded border">
                                    <div className="text-primary small fw-bold mb-1">Presenting Officer</div>
                                    <div className="fw-semibold text-dark">
                                      {poName || (poEmpNo ? `Emp No: ${poEmpNo}` : "Not Specified")}
                                      {poDesig ? ` (${poDesig})` : ""}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      {poEmpNo ? `Emp No: ${poEmpNo}` : "Emp No: -"}
                                      {poSource ? ` | Source: ${poSource}` : ""}
                                    </div>
                                    {poRemarks && (
                                      <div className="text-muted small mt-2">
                                        <strong>Remarks:</strong> {poRemarks}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        <h5 className="mb-3 text-primary fw-bold">Appellate Order & Appeal Documents</h5>
                        <div className="d-flex flex-wrap gap-3">
                          {/* Appellate Order File */}
                          {(appealData.aa_orderfile || appealData.urls?.aa_order_url || appealData.aa_order_url) && (
                            <button
                              onClick={() => viewDocument(appealData.urls?.aa_order_url || appealData.aa_order_url)}
                              className="btn btn-outline-danger d-flex align-items-center gap-2"
                              disabled={!(appealData.urls?.aa_order_url || appealData.aa_order_url)}
                            >
                              <i className="ti ti-file-text fs-5"></i> View Appellate Order
                            </button>
                          )}

                          {/* Employee Appeal Document */}
                          {(appealData.appeal_file || appealData.appeal_document || appealData.employee_appeal_file || appealData.appellant_file || appealData.appeal_url || appealData.urls?.appeal_url) && (
                            <button
                              onClick={() => viewDocument(appealData.urls?.appeal_url || appealData.appeal_url)}
                              className="btn btn-outline-primary d-flex align-items-center gap-2"
                              disabled={!(appealData.urls?.appeal_url || appealData.appeal_url)}
                            >
                              <i className="ti ti-file-description fs-5"></i> View Appeal Application Document
                            </button>
                          )}

                          {/* Corrigendum File */}
                          {(appealData.corrigendumfile || appealData.urls?.corrigendum_url || appealData.corrigendum_url) && (
                            <button
                              onClick={() => viewDocument(appealData.urls?.corrigendum_url || appealData.corrigendum_url)}
                              className="btn btn-outline-info d-flex align-items-center gap-2"
                              disabled={!(appealData.urls?.corrigendum_url || appealData.corrigendum_url)}
                            >
                              <i className="ti ti-file-symlink fs-5"></i> View Corrigendum
                            </button>
                          )}

                          {/* Original Penalty Order from Closed Stage */}
                          {(closedData?.finalorderfile || closedData?.urls?.final_order_url || closedData?.finalorder_url || appealData.closedDetails?.finalorder_url) && (
                            <button
                              onClick={() => viewDocument(closedData?.urls?.final_order_url || closedData?.finalorder_url || appealData.closedDetails?.finalorder_url)}
                              className="btn btn-outline-secondary d-flex align-items-center gap-2"
                              disabled={!(closedData?.urls?.final_order_url || closedData?.finalorder_url || appealData.closedDetails?.finalorder_url)}
                            >
                              <i className="ti ti-file-check fs-5"></i> View Original Penalty Order
                            </button>
                          )}

                          {!(appealData.aa_orderfile || appealData.urls?.aa_order_url || appealData.aa_order_url) &&
                            !(appealData.appeal_file || appealData.appeal_document || appealData.employee_appeal_file || appealData.appellant_file || appealData.appeal_url || appealData.urls?.appeal_url) &&
                            !(appealData.corrigendumfile || appealData.urls?.corrigendum_url || appealData.corrigendum_url) &&
                            !(closedData?.finalorderfile || closedData?.urls?.final_order_url || closedData?.finalorder_url || appealData.closedDetails?.finalorder_url) && (
                              <span className="text-muted fst-italic">No documents attached for this appeal.</span>
                            )}
                        </div>
                      </div>
                    </div>
                    {renderHistorySection(appealData, "appeal")}
                  </div>
                ) : (
                  renderNotReached("Appeal")
                ),
              },
              {
                label: (
                  <span>
                    <i className="ti ti-ban me-1"></i> Suspension
                  </span>
                ),
                key: "SUSPENSION",
                children: loading && activeTab === "SUSPENSION" ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : suspensionData ? (
                  <div>
                    {/* SECTION 1: Suspension Details */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-ban me-2 text-danger"></i> 1. Suspension Details
                        </h5>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            if (isClosedStage(suspensionData?.status || closedData?.status || generalCaseInfo?.status)) {
                              toast.error(CLOSED_CASE_EXACT_ERROR);
                              return;
                            }
                            navigate(`/dar-cases/sd-suspension-form?caseno=${caseno}`);
                          }}
                        >
                          <i className="ti ti-edit me-1"></i> Edit Suspension
                        </button>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4 mb-3">
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Suspension Type</div>
                            <div className="fw-semibold text-dark">
                              {suspensionData.susp_type || suspensionData.suspension_type || suspensionData.type || "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Start Date</div>
                            <div className="fw-semibold text-dark">
                              {(suspensionData.susp_start_date || suspensionData.start_date || suspensionData.suspension_start_date)
                                ? dayjs(suspensionData.susp_start_date || suspensionData.start_date || suspensionData.suspension_start_date).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Subsistence Allowance</div>
                            <div className="fw-semibold text-dark">
                              {(suspensionData.subsist_allow || suspensionData.subsistence_allowance || suspensionData.allowance) !== undefined &&
                               (suspensionData.subsist_allow || suspensionData.subsistence_allowance || suspensionData.allowance) !== null
                                ? `${suspensionData.subsist_allow || suspensionData.subsistence_allowance || suspensionData.allowance}%`
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Status</div>
                            <div>
                              <span className={`badge ${suspensionData.status === 'Closed' ? 'bg-success' : 'bg-warning text-dark'} px-2 py-1`}>
                                {suspensionData.status || "Active"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {(suspensionData.susp_reason || suspensionData.reason || suspensionData.remarks || suspensionData.description) && (
                          <div className="mt-3 p-3 bg-light rounded border">
                            <div className="text-muted small fw-medium mb-1">Suspension Reason / Remarks</div>
                            <div className="text-dark fw-medium" style={{ whiteSpace: "pre-wrap" }}>
                              {suspensionData.susp_reason || suspensionData.reason || suspensionData.remarks || suspensionData.description}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SECTION 2: Previous Actions */}
                    {suspensionData.previous_suspensions && suspensionData.previous_suspensions.length > 0 && (
                      <div className="card shadow-sm border-0 rounded-3 mb-4">
                        <div className="card-header bg-white border-bottom p-3">
                          <h5 className="mb-0 fw-bold">
                            <i className="ti ti-history me-2 text-secondary"></i> 2. Previous Actions
                          </h5>
                        </div>
                        <div className="card-body p-4">
                          <div className="table-responsive">
                            <table className="table table-hover border">
                              <thead className="table-light">
                                <tr>
                                  <th>S.No.</th>
                                  <th>Suspension No</th>
                                  <th>Type</th>
                                  <th>Start Date</th>
                                  <th>End Date</th>
                                  <th>Allowance</th>
                                  <th>Documents</th>
                                </tr>
                              </thead>
                              <tbody>
                                {suspensionData.previous_suspensions.map((prev: any, index: number) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{prev.suspension_no || "-"}</td>
                                    <td>{prev.susp_type || "-"}</td>
                                    <td>{prev.susp_start_date ? dayjs(prev.susp_start_date).format("DD MMM YYYY") : "-"}</td>
                                    <td>{prev.susp_end_date ? dayjs(prev.susp_end_date).format("DD MMM YYYY") : "-"}</td>
                                    <td>{prev.subsist_allow !== null && prev.subsist_allow !== undefined ? `${String(prev.subsist_allow).replace('%', '')}%` : "-"}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        {(prev.urls?.order_url || prev.orderfile) && (
                                          <button
                                            onClick={() => viewDocument(prev.urls?.order_url || prev.orderfile)}
                                            className="btn btn-sm btn-outline-primary"
                                            title="View Order"
                                          >
                                            <i className="ti ti-file-text"></i> Order
                                          </button>
                                        )}
                                        {(prev.urls?.revocation_url || prev.revocationfile) && (
                                          <button
                                            onClick={() => viewDocument(prev.urls?.revocation_url || prev.revocationfile)}
                                            className="btn btn-sm btn-outline-success"
                                            title="View Revocation"
                                          >
                                            <i className="ti ti-file-check"></i> Revocation
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* SECTION 3: Details of Charges & Charge Sheet Document */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-file-certificate me-2 text-warning"></i> 3. Details of Charges & Charge Sheet Document
                        </h5>
                        {(pendingData?.caseno || contemplatedData?.caseno) && (
                          <Link
                            to={`/dar-cases/pending-form?caseno=${caseno}`}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="ti ti-edit me-1"></i> Edit Charges
                          </Link>
                        )}
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4 mb-3">
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Type of Charges</div>
                            <div className="fw-semibold text-dark">
                              {pendingData?.type_of_charges || pendingData?.charge_type || pendingData?.chargetype || suspensionData?.type_of_charges || suspensionData?.charge_type || "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Date of Charge Sheet Issuance</div>
                            <div className="fw-semibold text-dark">
                              {(suspensionData.chargesheetdate || pendingData?.chargesheetdate || suspensionData.chargesheet_date || pendingData?.chargesheet_date)
                                ? dayjs(suspensionData.chargesheetdate || pendingData?.chargesheetdate || suspensionData.chargesheet_date || pendingData?.chargesheet_date).format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="text-muted small fw-medium mb-1">Charge Sheet Document</div>
                            {(suspensionData.chargesheet_url || suspensionData.urls?.chargesheet_url || suspensionData.chargesheetfile || pendingData?.chargesheet_url || pendingData?.urls?.chargesheet_url || pendingData?.chargesheetfile || contemplatedData?.approvalfile || contemplatedData?.approval_url) ? (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    suspensionData.chargesheet_url ||
                                    suspensionData.urls?.chargesheet_url ||
                                    pendingData?.chargesheet_url ||
                                    pendingData?.urls?.chargesheet_url ||
                                    suspensionData.chargesheetfile ||
                                    pendingData?.chargesheetfile ||
                                    contemplatedData?.approval_url ||
                                    contemplatedData?.approvalfile
                                  )
                                }
                                className="btn btn-sm btn-outline-primary mt-1 d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-text fs-5"></i> View Charge Sheet Document
                              </button>
                            ) : (
                              <div className="text-muted small mt-1 fst-italic">No Charge Sheet Document Attached</div>
                            )}
                          </div>
                        </div>

                        {/* Charges List */}
                        <div className="mb-4">
                          <div className="text-muted small fw-medium mb-2">Specific Charges</div>
                          {(suspensionData.charges && suspensionData.charges.length > 0) || (pendingData?.charges && pendingData.charges.length > 0) ? (
                            <div className="list-group list-group-flush border rounded-3 overflow-hidden shadow-none mb-0">
                              {((suspensionData.charges && suspensionData.charges.length > 0) ? suspensionData.charges : pendingData?.charges).map((charge: any, idx: number) => (
                                <div key={idx} className="list-group-item text-dark fw-medium d-flex align-items-start gap-2 bg-light-subtle py-2">
                                  <span className="text-secondary fw-bold" style={{ minWidth: "24px" }}>{idx + 1}.</span>
                                  <span>{charge.charge_description || charge.description || charge}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted small p-2 bg-light rounded">No specific charge list items recorded.</div>
                          )}
                        </div>

                        {/* Brief Description of Allegations */}
                        {(contemplatedData?.briefdescription || contemplatedData?.brief_description || contemplatedData?.allegations || pendingData?.remarks || suspensionData.brief_description) && (
                          <div className="p-3 bg-light rounded border">
                            <div className="text-muted small fw-medium mb-1">Brief Description of Allegations / Charges</div>
                            <div className="text-dark fw-medium" style={{ whiteSpace: "pre-wrap" }}>
                              {contemplatedData?.briefdescription || contemplatedData?.brief_description || contemplatedData?.allegations || pendingData?.remarks || suspensionData.brief_description}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SECTION 4: Disciplinary Authority & Case Info */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-gavel me-2 text-primary"></i> 4. Disciplinary Authority & Case Info
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4">
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">Case Type</div>
                            <div className="fw-semibold text-dark">
                              {suspensionData.casetype || pendingData?.casetype || contemplatedData?.casetype || "Major Penalty"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Name</div>
                            <div className="fw-semibold text-dark">
                              {suspensionData.daname || pendingData?.daname || contemplatedData?.daname || "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Designation</div>
                            <div className="fw-semibold text-dark">
                              {suspensionData.dadesignation || pendingData?.dadesignation || contemplatedData?.dadesignation || "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Employee No</div>
                            <div className="fw-semibold text-dark">
                              {suspensionData.daempno || pendingData?.daempno || contemplatedData?.daempno || "-"}
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-muted small fw-medium mb-1">DA Department</div>
                            <div className="fw-semibold text-dark">
                              {daDept || suspensionData.dadepartment || pendingData?.dadepartment || contemplatedData?.dadepartment || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 5: Suspension Orders & Documents */}
                    <div className="card shadow-sm border-0 rounded-3 mb-4">
                      <div className="card-header bg-white border-bottom p-3">
                        <h5 className="mb-0 fw-bold">
                          <i className="ti ti-files me-2 text-info"></i> 5. Suspension Orders & Documents
                        </h5>
                      </div>
                      <div className="card-body p-4">
                        <div className="row g-4 mb-4">
                          <div className="col-md-6">
                            <div className="text-muted small fw-medium mb-1">Suspension Order</div>
                            {(suspensionData.order_file || suspensionData.order_file_url || suspensionData.urls?.order_file_url) ? (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    suspensionData.order_file_url ||
                                    suspensionData.urls?.order_file_url ||
                                    suspensionData.order_file
                                  )
                                }
                                className="btn btn-sm btn-outline-primary mt-1 d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-text fs-5"></i> View Suspension Order
                              </button>
                            ) : (
                              <div className="text-muted small mt-1 fst-italic">No Suspension Order Attached</div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <div className="text-muted small fw-medium mb-1">Corrigendum Order</div>
                            {(suspensionData.corrigendum_order || suspensionData.corrigendum_file || suspensionData.corrigendum_url || suspensionData.urls?.corrigendum_url) ? (
                              <button
                                onClick={() =>
                                  viewDocument(
                                    suspensionData.corrigendum_url ||
                                    suspensionData.urls?.corrigendum_url ||
                                    suspensionData.corrigendum_order ||
                                    suspensionData.corrigendum_file
                                  )
                                }
                                className="btn btn-sm btn-outline-secondary mt-1 d-flex align-items-center gap-2"
                              >
                                <i className="ti ti-file-symlink fs-5"></i> View Corrigendum Order
                              </button>
                            ) : (
                              <div className="text-muted small mt-1 fst-italic">No Corrigendum Order Attached</div>
                            )}
                          </div>
                        </div>

                        {/* Revocation Section */}
                        {(suspensionData.revocation_order || suspensionData.revocation_file || suspensionData.revocation_url || suspensionData.urls?.revocation_url || suspensionData.revocation_effective_date || suspensionData.revocation_date) && (
                          <div className="mt-4 pt-4 border-top">
                            <h5 className="mb-3 text-success fw-bold">
                              <i className="ti ti-check me-2"></i> Revocation Details
                            </h5>
                            <div className="row g-4">
                              <div className="col-md-6">
                                <div className="text-muted small fw-medium mb-1">Effective Date</div>
                                <div className="fw-semibold text-dark">
                                  {(suspensionData.revocation_effective_date || suspensionData.revocation_date)
                                    ? dayjs(suspensionData.revocation_effective_date || suspensionData.revocation_date).format("DD MMM YYYY")
                                    : "-"}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="text-muted small fw-medium mb-1">Revocation Order</div>
                                {(suspensionData.revocation_order || suspensionData.revocation_file || suspensionData.revocation_url || suspensionData.urls?.revocation_url) ? (
                                  <button
                                    onClick={() =>
                                      viewDocument(
                                        suspensionData.revocation_url ||
                                        suspensionData.urls?.revocation_url ||
                                        suspensionData.revocation_order ||
                                        suspensionData.revocation_file
                                      )
                                    }
                                    className="btn btn-sm btn-outline-success mt-1 d-flex align-items-center gap-2"
                                  >
                                    <i className="ti ti-file-check fs-5"></i> View Revocation Order
                                  </button>
                                ) : (
                                  <div className="text-muted small mt-1 fst-italic">No Revocation Order Attached</div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {renderHistorySection(suspensionData)}
                  </div>
                ) : (
                  renderNotReached("Suspension")
                ),
              },
            ]}
          />
        </div>
      </div>

      {/* BYPASS MODAL */}
      {showBypassModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title fw-bold">
                  <i className="ti ti-alert-triangle me-2"></i> Bypass Case to Pending Stage
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBypassModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <p className="text-muted">
                  Bypassing this case will promote it directly to the <strong>Pending (Charge Sheet)</strong>{" "}
                  stage. This action is irreversible.
                </p>
                <div className="mb-3">
                  <label className="form-label fw-medium">
                    Reason for Bypass <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Provide a detailed reason for bypassing the contemplated stage..."
                    value={bypassReason}
                    maxLength={500}
                    onChange={e => setBypassReason(e.target.value)}
                    required
                  ></textarea>
                  <CharacterCounter value={bypassReason} maxLength={500} />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowBypassModal(false)}
                  disabled={bypassing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning text-dark fw-bold"
                  onClick={handleBypassSubmit}
                  disabled={bypassing}
                >
                  {bypassing ? "Bypassing..." : "Confirm Bypass"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DarCaseDetailsDashboard;
